/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Empty, Input, Modal, Radio, Steps } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { NotificationAlert, Loading } from '../../components';
import ViewUserTooltip from '../../components/view-user-tooltip';
import dayjs from 'dayjs';
import RadioForm from '../../components/radio-form/RadioForm';
import {
  getAnnualReview,
  saveAnnualReview,
  updateAnnualReviewByHr,
} from '../../services/annualReviewService';
import {
  AnnualReviewRequest,
  AnnualReviewResponse,
  questionCategory,
} from '../../types/getAnnualReviewApiTypes';
import RideSideCollapsibleComp from '../../components/RideSideCollapsibleComp';

import { MenteesProps } from '../../types';

import Header from '../../components/common/Header';
import SkillAlignmentComp from '../../components/SkillAlignmentComp';
import { getCompetencies } from '../../services/competenciesService';
import { CompetenciesResponse } from '../../types/competenciesTypes';
import {
  getUserProfilesFromStorage,
  statusColors,
  Statuses,
} from '../../helper';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MessagesIcon from '../icons/MessagesIcon';

const { TextArea } = Input;

interface FormData {
  organizationalData: {
    questions: {
      id: number;
      score: number | null;
      question: string;
      prevScore: number | null;
    }[];
    otherComments: string;
  };
  projectLevelData: {
    rolesAndResponsibilities: {
      questions: {
        id: number;
        score: number | null;
        question: string;
        prevScore: number | null;
      }[];
    };
    skillAlignments: {
      questions: {
        id: number;
        score: number | null;
        question: string;
        prevScore: number | null;
      }[];
    };
    otherComments: string;
  };
  valueAssignmentData: {
    questions: {
      id: number;
      score: number | null;
      question: string;
      prevScore: number | null;
    }[];
    otherComments: string;
  };
}

interface AnnualReviewProps {
  selectedMentee: MenteesProps;
  cycleId: string;
  type: 'mentor' | 'hr';
  cycleStatus: string | null;
}

const AnnualReview = ({
  selectedMentee,
  cycleId,
  type,
  cycleStatus,
}: AnnualReviewProps) => {
  const menteeId = selectedMentee?.id;
  const isHR = type === 'hr'; // HR is disabled mode for all forms
  const isFormDisabled = cycleStatus === Statuses.Closed;

  const [loading, setLoading] = useState(false);
  const [saveInprogress, setSaveInprogress] = useState(false);
  const [submitInprogress, setSubmitInprogress] = useState(false);
  const [skillAlignmentLoading, setSkillAlignmentLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    organizationalData: { questions: [], otherComments: '' },
    projectLevelData: {
      rolesAndResponsibilities: { questions: [] },
      skillAlignments: { questions: [] },
      otherComments: '',
    },
    valueAssignmentData: { questions: [], otherComments: '' },
  });
  const [skillAlignmentData, setSkillAlignmentData] =
    useState<CompetenciesResponse | null>(null);
  const [response, setResponse] = useState<AnnualReviewResponse | null>(null);
  const myRef = useRef<any>();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [validForSubmit, setValidForSubmit] = useState(false);
  const [validForSave, setValidForSave] = useState(false);
  const navigate = useNavigate();
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const [promotionRecommendation, setPromotionRecommendation] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    const fetchProfilePic = async () => {
      if (!selectedMentee) return;
      try {
        const res = await getUserProfilesFromStorage();
        const profile = res.data.find((u: any) => u.id === selectedMentee.id);

        if (profile?.file instanceof Blob) {
          const objectUrl = URL.createObjectURL(profile.file);
          setProfilePicUrl(objectUrl);
        }
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      }
    };

    fetchProfilePic();
  }, [selectedMentee]);

  const fetchAnnualReview = async () => {
    if (!menteeId) return;

    setLoading(true);
    try {
      const res = await getAnnualReview(cycleId, menteeId);
      if (res.error) {
        myRef.current?.call('error', res.error?.message);
        return;
      }
      setResponse(res.data as AnnualReviewResponse);
      questionMapper(res.data as AnnualReviewResponse);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetencies = async () => {
    if (!menteeId) return;

    setSkillAlignmentLoading(true);
    try {
      const res = await getCompetencies(menteeId, 'annual-review');
      if (res.error) {
        myRef.current?.call('error', res.error?.message);
        return;
      }
      setSkillAlignmentData(res.data as CompetenciesResponse);
    } finally {
      setSkillAlignmentLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnualReview();
  }, []);

  useEffect(() => {
    fetchCompetencies();
  }, []);

  // map the questions to the form data from the api response
  const questionMapper = (data: AnnualReviewResponse) => {
    if (data.promotionRecommendation !== undefined) {
      setPromotionRecommendation(data.promotionRecommendation);
    }
    const organizational = data.reviewCategoryItems.find(
      (item) => item.category.type === questionCategory.Organizational
    );
    if (organizational) {
      setFormData((prev) => {
        return {
          ...prev,
          organizationalData: {
            questions:
              organizational.reviewStatementItems.map((item) => ({
                id: item.statement.id,
                score: item.score,
                question: item.statement.description,
                prevScore: item.score,
              })) || [],
            otherComments: organizational.comment || '',
          },
        };
      });
    }
    const rolesAndResposibilities = data.reviewCategoryItems.find(
      (item) =>
        item.category.type === questionCategory.ProjectLevel &&
        item.category.id === 2
    );
    if (rolesAndResposibilities) {
      setFormData((prev) => {
        return {
          ...prev,
          projectLevelData: {
            rolesAndResponsibilities: {
              questions:
                rolesAndResposibilities.reviewStatementItems.map((item) => ({
                  id: item.statement.id,
                  score: item.score,
                  question: item.statement.description,
                  prevScore: item.score,
                })) || [],
            },
            skillAlignments: {
              questions: prev.projectLevelData.skillAlignments.questions,
            },
            otherComments: rolesAndResposibilities.comment || '',
          },
        };
      });
    }

    const skillAllignment = data.reviewCategoryItems.find(
      (item) =>
        item.category.type === questionCategory.ProjectLevel &&
        item.category.id === 3
    );
    if (skillAllignment) {
      setFormData((prev) => {
        return {
          ...prev,
          projectLevelData: {
            rolesAndResponsibilities: {
              questions:
                prev.projectLevelData.rolesAndResponsibilities.questions,
            },
            skillAlignments: {
              questions:
                skillAllignment.reviewStatementItems.map((item) => ({
                  id: item.statement.id,
                  score: item.score,
                  question: item.statement.description,
                  prevScore: item.score,
                })) || [],
            },
            otherComments: skillAllignment.comment || '',
          },
        };
      });
    }

    const valueAssignment = data.reviewCategoryItems.find(
      (item) => item.category.type === questionCategory.ValueAssignment
    );
    if (valueAssignment) {
      setFormData((prev) => {
        return {
          ...prev,
          valueAssignmentData: {
            questions:
              valueAssignment.reviewStatementItems.map((item) => ({
                id: item.statement.id,
                score: item.score,
                question: item.statement.description,
                prevScore: item.score,
              })) || [],
            otherComments: valueAssignment.comment || '',
          },
        };
      });
    }
  };

  const hasScoreChanged = (
    data: FormData,
    type: 'save' | 'submit'
  ): boolean | any => {
    const arr: any[] = [];
    const checkQuestions = (
      questions: { score: number | null; prevScore: number | null }[]
    ) => {
      if (type === 'submit')
        return !questions.some((question) => question.score === null);
      if (type === 'save')
        return questions.some(
          (question) => question.score !== question.prevScore
        );
    };

    //checkQuestions(formData.organizationalData.questions) ? true : false;
    if (checkQuestions(data.organizationalData.questions)) {
      arr[0] = true;
    } else {
      arr[0] = false;
    }
    if (
      checkQuestions(data.projectLevelData.rolesAndResponsibilities.questions)
    ) {
      arr[1] = true;
    } else {
      arr[1] = false;
    }

    if (checkQuestions(data.projectLevelData.skillAlignments.questions)) {
      arr[2] = true;
    } else {
      arr[2] = false;
    }

    if (checkQuestions(data.valueAssignmentData.questions)) {
      arr[3] = true;
    } else {
      arr[3] = false;
    }

    if (type === 'submit') return !arr.some((item) => item === false);
    if (type === 'save') return arr.some((item) => item === true);
  };

  const handleNext = () => {
    if (currentStep !== 2) {
      setCurrentStep((prev) => prev + 1);
      setValidForSubmit(hasScoreChanged(formData, 'submit'));

      return;
    }

    // open confirmation modal
    setConfirmationModalOpen(true);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSave = async (
    type: 'save' | 'submit',
    promotionRecommendation: boolean | null = null
  ) => {
    if (!response) return;

    if (type === 'save') {
      setSaveInprogress(true);
    } else {
      setSubmitInprogress(true);
    }

    const data: any[] = response.reviewCategoryItems.map((item) => {
      if (item.category.type === questionCategory.Organizational) {
        return {
          ...item,
          reviewStatementItems: item.reviewStatementItems.map((rsi) => {
            const formDataItem = formData.organizationalData.questions.find(
              (q) => q.id === rsi.statement.id
            );
            return {
              ...rsi,
              score: formDataItem?.score,
            };
          }),
          comment: formData.organizationalData.otherComments,
        };
      } else if (
        item.category.type === questionCategory.ProjectLevel &&
        item.category.id === 2
      ) {
        return {
          ...item,
          reviewStatementItems: item.reviewStatementItems.map((rsi) => {
            const formDataItem =
              formData.projectLevelData.rolesAndResponsibilities.questions.find(
                (q) => q.id === rsi.statement.id
              );
            return {
              ...rsi,
              score: formDataItem?.score,
            };
          }),
          comment: formData.projectLevelData.otherComments,
        };
      } else if (
        item.category.type === questionCategory.ProjectLevel &&
        item.category.id === 3
      ) {
        return {
          ...item,
          reviewStatementItems: item.reviewStatementItems.map((rsi) => {
            const formDataItem =
              formData.projectLevelData.skillAlignments.questions.find(
                (q) => q.id === rsi.statement.id
              );
            return {
              ...rsi,
              score: formDataItem?.score,
            };
          }),
          comment: formData.projectLevelData.otherComments,
        };
      } else if (item.category.type === questionCategory.ValueAssignment) {
        return {
          ...item,
          reviewStatementItems: item.reviewStatementItems.map((rsi) => {
            const formDataItem = formData.valueAssignmentData.questions.find(
              (q) => q.id === rsi.statement.id
            );
            return {
              ...rsi,
              score: formDataItem?.score,
            };
          }),
          comment: formData.valueAssignmentData.otherComments,
        };
      }
    });

    if (!data) return;

    const request: AnnualReviewRequest = {
      ...response,
      status:
        type === 'submit'
          ? Statuses.Completed
          : type === 'save'
            ? Statuses.InProgress
            : response.status,
      reviewCategoryItems: data,
      frameworkValue: null,
      promotionRecommendation: promotionRecommendation,
    };

    if (isHR && type === 'save') {
      const { error } = await updateAnnualReviewByHr(
        cycleId,
        menteeId,
        request
      );
      setSaveInprogress(false);
      if (error) return myRef.current?.call('error', error?.message);
      myRef.current?.call('success', 'Changes saved successfully');
      // window.setTimeout(() => {
      //   window.location.reload();
      // }, 3000);
      window.location.reload();
    } else {
      const { error } = await saveAnnualReview(cycleId, menteeId, request);
      setSubmitInprogress(false);
      setSaveInprogress(false);

      if (error) return myRef.current?.call('error', error?.message);

      if (type === 'submit') {
        myRef.current?.call('success', 'Feedback form submitted successfully');

        fetchAnnualReview();
        fetchCompetencies();
      } else if (type === 'save') {
        myRef.current?.call('success', 'Changes saved successfully');
        if (response) {
          setResponse({ ...response, status: Statuses.InProgress });
        }
        //setValidForSave(false);
      }
    }
  };

  const handleRadioChange = (id: number, score: number, category: string) => {
    const updatedFormData = { ...formData };

    if (category === 'organizational') {
      updatedFormData.organizationalData.questions =
        updatedFormData.organizationalData.questions.map((item) =>
          item.id === id ? { ...item, score: score } : item
        );
    } else if (category === 'rolesAndResponsibilities') {
      updatedFormData.projectLevelData.rolesAndResponsibilities.questions =
        updatedFormData.projectLevelData.rolesAndResponsibilities.questions.map(
          (item) => (item.id === id ? { ...item, score: score } : item)
        );
    } else if (category === 'valueAssignment') {
      updatedFormData.valueAssignmentData.questions =
        updatedFormData.valueAssignmentData.questions.map((item) =>
          item.id === id ? { ...item, score: score } : item
        );
    }

    setFormData(updatedFormData);
    setValidForSubmit(hasScoreChanged(updatedFormData, 'submit'));
    setValidForSave(hasScoreChanged(updatedFormData, 'save'));
  };

  const handleCommentChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    category: string
  ) => {
    setFormData((prev) => {
      const newFormData = { ...prev };
      if (category === 'organizational') {
        newFormData.organizationalData.otherComments = e.target.value;
      } else if (category === 'projectLevel') {
        newFormData.projectLevelData.otherComments = e.target.value;
      } else if (category === 'valueAssignment') {
        newFormData.valueAssignmentData.otherComments = e.target.value;
      }
      return newFormData;
    });
  };

  const handleDropdownChange = (value: number, id: number) => {
    const updatedFormData = {
      ...formData,
      projectLevelData: {
        rolesAndResponsibilities: {
          questions:
            formData.projectLevelData.rolesAndResponsibilities.questions,
        },
        skillAlignments: {
          questions: formData.projectLevelData.skillAlignments.questions.map(
            (item) => (item.id === id ? { ...item, score: value } : item)
          ),
        },
        otherComments: formData.projectLevelData.otherComments,
      },
    };

    setFormData(updatedFormData);

    setValidForSubmit(hasScoreChanged(updatedFormData, 'submit'));
    setValidForSave(hasScoreChanged(updatedFormData, 'save'));
  };

  const toSentenceCase = (str: string) => {
    return str
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase());
  };

  const OrganizationalLevelObjectiveComponent = () => {
    return (
      <>
        <div className="flex justify-start items-center gap-x-6 mb-10">
          <Header title="Organizational-level Objectives" />
          {response?.status === Statuses.Completed && isHR && (
            <MarksTag
              marks={
                response?.reviewCategoryItems.find(
                  (item) =>
                    item.category.type === questionCategory.Organizational
                )?.categoryScore || 0
              }
            />
          )}
        </div>
        <RadioForm
          isDisabled={
            isFormDisabled || (!isHR && response?.status === Statuses.Completed)
          }
          data={formData.organizationalData.questions}
          onChange={(id, score) =>
            handleRadioChange(id, score, 'organizational')
          }
        />
      </>
    );
  };

  const ProjectLevelObjectiveComponent = () => {
    return (
      <>
        <Header title="Project-level Objectives" />
        <div className="flex justify-start items-center gap-x-6 mt-5 mb-6">
          <Header title="Roles & Responsibilities" className="text-[#2E7D32]" />
          {response?.status === Statuses.Completed && isHR && (
            <MarksTag
              marks={
                response?.reviewCategoryItems.find(
                  (item) =>
                    item.category.type === questionCategory.ProjectLevel &&
                    item.category.id === 2
                )?.categoryScore || 0
              }
            />
          )}
        </div>

        <RadioForm
          isDisabled={
            isFormDisabled || (!isHR && response?.status === Statuses.Completed)
          }
          data={formData.projectLevelData.rolesAndResponsibilities.questions}
          onChange={(id, score) =>
            handleRadioChange(id, score, 'rolesAndResponsibilities')
          }
        />

        {skillAlignmentData && (
          <div className="mb-6 mt-10">
            <div className="flex justify-start items-center gap-x-6 mt-4  mb-6">
              <Header title="Skill Alignment" className="text-[#2E7D32]" />
              {response?.status === Statuses.Completed && isHR && (
                <MarksTag
                  marks={
                    response?.reviewCategoryItems.find(
                      (item) =>
                        item.category.type === questionCategory.ProjectLevel &&
                        item.category.id === 3
                    )?.categoryScore || 0
                  }
                />
              )}
            </div>
            <SkillAlignmentComp
              skills={
                skillAlignmentData ? skillAlignmentData.competencyItems : []
              }
              formData={formData.projectLevelData.skillAlignments.questions}
              isLoading={skillAlignmentLoading || loading}
              onLevelChange={handleDropdownChange}
              disabled={
                isFormDisabled ||
                (!isHR && response?.status === Statuses.Completed)
              }
            />
          </div>
        )}
      </>
    );
  };

  const ValueAssignmentComponent = () => {
    return (
      <>
        <div className="flex justify-start items-center gap-x-6">
          <Header title="Value Alignment" />
          {response?.status === Statuses.Completed && isHR && (
            <MarksTag
              marks={
                response?.reviewCategoryItems.find(
                  (item) =>
                    item.category.type === questionCategory.ValueAssignment
                )?.categoryScore || 0
              }
            />
          )}
        </div>

        {!isHR && response?.status !== Statuses.Completed && (
          <Header
            className="mt-4 mb-4 font-normal text-gray-600"
            title="How well does the mentee understand & practice the company's core values?"
          />
        )}

        <RadioForm
          isDisabled={
            isFormDisabled || (!isHR && response?.status === Statuses.Completed)
          }
          data={formData.valueAssignmentData.questions}
          options={[
            'Does Not Demonstrate',
            'Demonstrates Sometimes',
            'Consistently Demonstrates',
          ]}
          onChange={(id, score) =>
            handleRadioChange(id, score, 'valueAssignment')
          }
        />
      </>
    );
  };

  const renderStepContent = (currentStep: number) => {
    switch (currentStep) {
      case 0:
        return <OrganizationalLevelObjectiveComponent />;

      case 1:
        return <ProjectLevelObjectiveComponent />;

      case 2:
        return <ValueAssignmentComponent />;
    }
  };

  return (
    <>
      <>
        {/** Confirmation modal */}
        <Modal
          open={confirmationModalOpen}
          onCancel={() => {
            setConfirmationModalOpen(false);
            setPromotionRecommendation(null);
          }}
          title="Submit Annual Review"
          okText="Submit"
          cancelText="Cancel"
          onOk={() => {
            handleSave('submit', promotionRecommendation);
            setConfirmationModalOpen(false);
            setPromotionRecommendation(null);
          }}
          okButtonProps={{ disabled: promotionRecommendation === null }}
        >
          <div className="my-4">
            <hr />
          </div>
          <p className="font-semibold mb-2">
            Do you recommend your mentee for a promotion?
          </p>
          <div className="mt-4">
            <Radio.Group
              onChange={(e) => setPromotionRecommendation(e.target.value)}
              value={promotionRecommendation}
              disabled={isFormDisabled}
            >
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </div>
          <p className="mt-6">
            Submitted reviews can't be edited, are you sure you want to submit
            this annual review?
          </p>
        </Modal>

        <div className="flex flex-col gap-y-[10px] p-[10px] h-full">
          <div className="text-[20px] font-[500] flex justify-start items-center gap-2">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />{' '}
            <ViewUserTooltip
              user={{ ...selectedMentee, profilePic: profilePicUrl || '' }}
            />
            <p> - Annual Review</p>
          </div>
          {/* <div className="w-32 mb-6 text-sm">
          <Button icon={<DownloadOutlined />} size="small">
            Export to Excel
          </Button>
        </div> */}
          {loading ? (
            <Loading />
          ) : !response ? (
            <Empty />
          ) : isHR && response?.status !== Statuses.Completed ? (
            <Empty />
          ) : (
            <div className="mt-7 h-full">
              <div className="flex justify-between items-center ">
                <div className="flex justify-start items-center gap-3">
                  <div className="bg-[#E6F7FF] p-4 rounded-md border-2 border-[#91D5FF]">
                    <div className="grid grid-cols-[auto_auto_1fr] gap-x-4 items-center">
                      <div className="text-[#666666] text-md mb-1 font-semibold">
                        Expected PAR Points (Tier {selectedMentee.tier}) :
                      </div>
                      <div className="text-lg  text-[#262626] ">
                        {response?.expectedParPointScore || 0}
                      </div>

                      {(response?.expectedParPointScore ?? 0) <=
                      (response?.parPointScore ?? 0) ? (
                        <div className="text-[#52C41A] text-sm font-medium border border-[#52C41A] px-2 py-1 bg-[#F6FFED] rounded-full justify-self-end">
                          Requirement Achieved
                        </div>
                      ) : (
                        <div className="text-[#FF4D4F] text-sm font-medium border border-[#FF4D4F] px-2 py-1 bg-[#FFF1F0] rounded-full justify-self-end">
                          Requirement Not Achieved
                        </div>
                      )}

                      <div className="text-[#666666] text-md mb-1 font-semibold">
                        Current PAR Points :
                      </div>
                      <div className="text-lg  text-[#262626]">
                        {response?.parPointScore || 'Not Available'}
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#E9FFE6] p-4 rounded-md max-w-[400px] border-2 border-[#3A9800]">
                    <div className="text-[#666666] text-md mb-1 font-semibold">
                      Client Utilization
                    </div>
                    <div className="text-lg  text-[#262626] ">
                      {response?.clientUtilization !== 0 &&
                      response?.clientUtilization !== null
                        ? response?.clientUtilization + '%'
                        : 'Not Available'}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center ">
                    <div className="ml-auto flex items-center gap-4">
                      <div className="flex flex-col rounded-[5px] px-3 py-2">
                        <div>Mentor Submission Deadline</div>
                        <div className="text-base font-bold">
                          {dayjs(response?.reviewCycle.mentorDeadline).format(
                            'MMM DD YYYY'
                          )}
                        </div>
                      </div>

                      <div
                        className="flex flex-col rounded-[5px] text-white px-3 py-2"
                        style={{
                          background: `${response?.status === Statuses.InProgress ? statusColors[Statuses.InProgress] : response?.status === Statuses.Completed ? statusColors[Statuses.Completed] : response?.status === Statuses.NotStarted ? statusColors[Statuses.NotStarted] : statusColors[Statuses.Draft]}`,
                        }}
                      >
                        <div>Annual Review Progress</div>
                        <div className="text-base font-bold">
                          {toSentenceCase(response?.status || '')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`flex  h-full ${!isCollapsed && 'gap-x-8'} `}>
                <div className="flex-1 relative">
                  {isCollapsed && (
                    <>
                      <div className="absolute top-10 right-0 shadow-xl rounded-full hover:shadow-md transition-all duration-300">
                        <Button
                          color="#F8F8F8"
                          type="text"
                          icon={<MessagesIcon width={24} height={24} />}
                          onClick={() => setIsCollapsed(!isCollapsed)}
                          style={{
                            padding: '22px',
                            border: 'solid 2px #E3E3E3',
                            backgroundColor: '#F8F8F8',
                            borderRadius: '18px',
                          }}
                        />
                      </div>
                    </>
                  )}
                  {/** Left side */}

                  {!isHR && response?.status !== Statuses.Completed && (
                    <div className=" mb-10 mt-14">
                      <div className="flex justify-center">
                        <Steps
                          current={currentStep}
                          labelPlacement="vertical"
                          size="default"
                          onChange={(value) => {
                            setCurrentStep(value);
                            setValidForSubmit(
                              hasScoreChanged(formData, 'submit')
                            );
                          }}
                          items={[
                            {
                              title: (
                                <p
                                  className={`font-semibold ${currentStep === 0 || currentStep === 1 || currentStep === 2 ? 'text-[#1677ff]' : ''} min-w-40 ml-[-13px]`}
                                >
                                  Organizational-level Objectives
                                </p>
                              ),
                            },
                            {
                              title: (
                                <p
                                  className={`font-semibold ${currentStep === 1 || currentStep === 2 ? 'text-[#1677ff]' : ''} min-w-40 ml-[-13px]`}
                                >
                                  Project-Level Objectives
                                </p>
                              ),
                            },
                            {
                              title: (
                                <p
                                  className={`font-semibold ${currentStep === 2 ? 'text-[#1677ff]' : ''} min-w-40 ml-[-13px]`}
                                >
                                  Value Alignment
                                </p>
                              ),
                            },
                          ]}
                          className="w-[800px]"
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex-1 mt-14">
                    <div className="mb-8">
                      {!isHR &&
                        response?.status !== Statuses.Completed &&
                        renderStepContent(currentStep)}
                      {(isHR || response?.status === Statuses.Completed) && (
                        <>
                          {/** HR will see in a single view */}

                          {/** Organizational Level */}
                          <OrganizationalLevelObjectiveComponent />
                          {/** Other comments for Organizational Level */}
                          <div className="mb-10 mt-6">
                            <h3 className="text-lg font-medium mb-2">
                              Other Comments
                            </h3>
                            <TextArea
                              disabled={
                                isFormDisabled ||
                                (!isHR &&
                                  response?.status === Statuses.Completed)
                              }
                              rows={4}
                              value={formData.organizationalData.otherComments}
                              onChange={(e) =>
                                handleCommentChange(e, 'organizational')
                              }
                            />
                          </div>

                          {/** Project Level */}
                          <ProjectLevelObjectiveComponent />
                          {/** Other comments for Project Level */}
                          <div className="mb-10 mt-6">
                            <h3 className="text-lg font-medium mb-2">
                              Other Comments
                            </h3>
                            <TextArea
                              disabled={
                                isFormDisabled ||
                                (!isHR &&
                                  response?.status === Statuses.Completed)
                              }
                              rows={4}
                              value={formData.projectLevelData.otherComments}
                              onChange={(e) =>
                                handleCommentChange(e, 'projectLevel')
                              }
                            />
                          </div>

                          {/** Value Assignment */}
                          <ValueAssignmentComponent />
                          {/** Other comments for Value Assignment */}
                          <div className="mb-10 mt-6">
                            <h3 className="text-lg font-medium mb-2">
                              Other Comments
                            </h3>
                            <TextArea
                              disabled={
                                isFormDisabled ||
                                (!isHR &&
                                  response?.status === Statuses.Completed)
                              }
                              rows={4}
                              value={formData.valueAssignmentData.otherComments}
                              onChange={(e) =>
                                handleCommentChange(e, 'valueAssignment')
                              }
                            />
                          </div>

                          {!isHR && response?.status === Statuses.Completed && (
                            <div className="mb-10 mt-6">
                              <h3 className="text-lg font-medium mb-2">
                                Do you recommend your mentee for a promotion?
                              </h3>
                              <div className="border rounded-md p-2 w-full">
                                <p>
                                  {promotionRecommendation === true
                                    ? 'Yes'
                                    : promotionRecommendation === false
                                      ? 'No'
                                      : 'Not Available'}
                                </p>
                              </div>
                            </div>
                          )}

                          {isHR && response?.status === Statuses.Completed && (
                            <>
                              <div className="mb-10 mt-6">
                                <h3 className="text-lg font-medium mb-2">
                                  Do you recommend your mentee for a promotion?
                                </h3>
                                <div className="border rounded-md p-2 w-full">
                                  <p>
                                    {promotionRecommendation === true
                                      ? 'Yes'
                                      : promotionRecommendation === false
                                        ? 'No'
                                        : 'Not Available'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex justify-end mt-4">
                                <Button
                                  type="primary"
                                  onClick={() =>
                                    handleSave('save', promotionRecommendation)
                                  }
                                  loading={saveInprogress}
                                  disabled={
                                    isFormDisabled ||
                                    !validForSave ||
                                    saveInprogress ||
                                    submitInprogress
                                  }
                                >
                                  Save Changes
                                </Button>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>

                    {!isHR && response?.status !== Statuses.Completed && (
                      <div className="mb-6">
                        <h3 className="text-lg font-medium mb-2">
                          Other Comments
                        </h3>
                        {/** This other comments render based on the current step */}
                        <TextArea
                          rows={4}
                          value={
                            currentStep === 0
                              ? formData.organizationalData.otherComments
                              : currentStep === 1
                                ? formData.projectLevelData.otherComments
                                : formData.valueAssignmentData.otherComments
                          }
                          onChange={(e) =>
                            handleCommentChange(
                              e,
                              currentStep === 0
                                ? 'organizational'
                                : currentStep === 1
                                  ? 'projectLevel'
                                  : 'valueAssignment'
                            )
                          }
                          placeholder="Enter any additional comments..."
                        />
                      </div>
                    )}

                    {!isHR && response?.status !== Statuses.Completed && (
                      <div
                        className={`flex ${currentStep === 0 ? 'justify-end' : 'justify-between'}`}
                      >
                        {currentStep !== 0 && (
                          <Button
                            disabled={currentStep === 0}
                            onClick={handleBack}
                          >
                            Back
                          </Button>
                        )}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleSave('save')}
                            loading={saveInprogress}
                            //disabled={saveInprogress || !validForSubmit}
                            disabled={
                              isFormDisabled ||
                              !validForSave ||
                              saveInprogress ||
                              submitInprogress
                            }
                          >
                            Save Changes
                          </Button>
                          <Button
                            type="primary"
                            onClick={handleNext}
                            loading={submitInprogress}
                            //disabled={saveInprogress || !validForSubmit}
                            disabled={
                              isFormDisabled ||
                              saveInprogress ||
                              submitInprogress ||
                              (currentStep === 2 ? !validForSubmit : false)
                            }
                          >
                            {currentStep === 2 ? 'Submit' : 'Next'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Sidebar */} {/** Sticky sidebar  to the right  */}
                <div className=" mt-5 ">
                  {/** Collapsible sidebar */}
                  <RideSideCollapsibleComp
                    isCollapsed={isCollapsed}
                    setIsCollapsed={setIsCollapsed}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </>

      <NotificationAlert ref={myRef} />
    </>
  );
};

export default AnnualReview;

interface MarksTagProps {
  marks: number;
}

const MarksTag = ({ marks }: MarksTagProps) => {
  return (
    <div className="text-[#52C41A] text-sm font-medium border border-[#52C41A] px-3 py-1 bg-[#F6FFED] rounded-full justify-self-end">
      Average score - {marks.toFixed(2)}
    </div>
  );
};
